export const SUPPORTED_SCOPES = [
  "openid",
  "email",
  "profile",
  "groups",
] as const;
export type Scope = (typeof SUPPORTED_SCOPES)[number];

export const TOKEN_ENDPOINT_AUTH_METHODS = [
  "client_secret_basic",
  "client_secret_post",
  "none",
] as const;
export type TokenEndpointAuthMethod =
  (typeof TOKEN_ENDPOINT_AUTH_METHODS)[number];

/** Cloudflare Access application types that expose an MCP surface. */
export const MCP_APPLICATION_TYPES = ["mcp", "mcp_portal"] as const;

export const ACCESS_POLICY_DECISIONS = [
  "allow",
  "deny",
  "non_identity",
  "bypass",
  "service_auth",
] as const;
export type AccessPolicyDecision = (typeof ACCESS_POLICY_DECISIONS)[number];

/** `URL.hostname` keeps IPv6 literals bracketed, so `[::1]` is the only form. */
const LOOPBACK_HOSTS = ["localhost", "127.0.0.1", "[::1]"];

/** True for the loopback origins that may serve plain HTTP during development. */
export function isLoopbackUrl(url: URL): boolean {
  return LOOPBACK_HOSTS.includes(url.hostname);
}

export const USERNAME_PATTERN = /^[a-z0-9][a-z0-9._@-]{2,63}$/;
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const PASSWORD_MIN_LENGTH = 12;

/**
 * Shared scope rule for both the OAuth request validator and the application
 * registry validator. It is a predicate rather than a throwing assertion so
 * each caller keeps its own error type and wording.
 */
export function scopesAreSupported(scopes: readonly unknown[]): boolean {
  return (
    scopes.includes("openid") &&
    scopes.every((scope) => SUPPORTED_SCOPES.includes(scope as Scope))
  );
}

export function isAccessPolicyDecision(
  value: unknown,
): value is AccessPolicyDecision {
  return ACCESS_POLICY_DECISIONS.includes(value as AccessPolicyDecision);
}

/** Cloudflare's API calls the Service Auth action `non_identity`. */
export function isServiceAuthDecision(
  value: AccessPolicyDecision | "unknown",
): boolean {
  return value === "service_auth" || value === "non_identity";
}
